import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';

const emptyFaq = () => ({ q: '', a: '' });

// ── ALL HELPER COMPONENTS DEFINED OUTSIDE FaqEditor ─────────────────────────
// Defining them inside causes React to treat them as new component types on
// every render, unmounting/remounting DOM nodes and stealing input focus.

const SectionHeader = ({ label }) => (
  <h6 className="text-primary fw-bold text-uppercase small mb-4" style={{ letterSpacing: '1px' }}>
    {label}
  </h6>
);

const FieldRow = ({ label, children }) => (
  <div className="mb-3">
    <Form.Label className="small fw-bold">{label}</Form.Label>
    {children}
  </div>
);

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────

const FaqEditor = ({ workingData, updateSection, onSave, isSaving }) => {

  const [fq, setFq] = useState(() => ({
    heroTitle:    'Frequently Asked Questions',
    heroSubtitle: 'Find answers to the most common questions about our services',
    heroBgImage:  '',
    ctaTitle:     'Still have questions?',
    ctaText:      'Our support team is happy to assist you with any queries.',
    ctaButton:    'Contact Support',
    ctaLink:      '/contact-us',
    faqs:         [],
    // DB wins
    ...workingData.faq,
  }));

  // ── pure local setters ───────────────────────────────────────────────────
  const set = (key, val) => setFq(prev => ({ ...prev, [key]: val }));

  const addFaq    = ()            => setFq(prev => ({ ...prev, faqs: [...prev.faqs, emptyFaq()] }));
  const removeFaq = (idx)         => setFq(prev => ({ ...prev, faqs: prev.faqs.filter((_, i) => i !== idx) }));
  const updateFaq = (idx, field, val) => setFq(prev => {
    const arr = [...prev.faqs];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...prev, faqs: arr };
  });

  const moveFaq = (idx, dir) => setFq(prev => {
    const arr  = [...prev.faqs];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return prev;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    return { ...prev, faqs: arr };
  });

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    updateSection('faq', fq);
    if (onSave) await onSave(fq);
    toast.success('FAQ saved successfully!');
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={11}>
          <Card className="shadow-sm border-0 rounded-4">

            <Card.Header className="bg-white py-3 border-bottom">
              <h5 className="fw-bold mb-0">
                <i className="ri-question-answer-line me-2 text-primary" />
                FAQ — Content Editor
              </h5>
             
            </Card.Header>

            <Card.Body className="p-0">
              <Accordion flush>

                {/* ── 1. HERO ─────────────────────────────────── */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <i className="ri-image-2-line me-2 text-primary" />
                    <strong>Hero Section</strong>
                    <span className="ms-2 text-muted small">— Page banner title &amp; background</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="1. Hero Banner" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="Hero Title">
                          <Form.Control
                            value={fq.heroTitle}
                            placeholder="Frequently Asked Questions"
                            onChange={e => set('heroTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Hero Subtitle">
                          <Form.Control
                            value={fq.heroSubtitle}
                            placeholder="Find answers to the most common questions..."
                            onChange={e => set('heroSubtitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Background Image URL">
                          <Form.Control
                            value={fq.heroBgImage}
                            placeholder="https://images.unsplash.com/..."
                            onChange={e => set('heroBgImage', e.target.value)}
                          />
                          {fq.heroBgImage && (
                            <img
                              src={fq.heroBgImage}
                              alt="preview"
                              className="mt-2 rounded"
                              style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', opacity: 0.85 }}
                              onError={e => e.target.style.display = 'none'}
                            />
                          )}
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 2. QUESTIONS & ANSWERS ──────────────────── */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <i className="ri-list-check me-2 text-primary" />
                    <strong>Questions &amp; Answers</strong>
                    <span className="ms-2 text-muted small">
                      — {fq.faqs.length} question{fq.faqs.length !== 1 ? 's' : ''} added
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="2. FAQ Items" />

                    {fq.faqs.length === 0 && (
                      <div className="text-center py-4 text-muted border rounded bg-light mb-3">
                        <i className="ri-question-line" style={{ fontSize: '32px', display: 'block', marginBottom: '8px', opacity: 0.4 }} />
                        <p className="small mb-0">No questions yet. Click "Add Question" to get started.</p>
                      </div>
                    )}

                    {fq.faqs.map((item, i) => (
                      <div key={i} className="border rounded p-3 pt-4 mb-3 bg-light position-relative">

                        {/* Controls: move up/down + delete — stopPropagation prevents Accordion stealing click */}
                        <div className="d-flex gap-1" style={{ position: 'absolute', top: '8px', right: '10px', zIndex: 10 }}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={i === 0}
                            style={{ padding: '2px 7px' }}
                            title="Move up"
                            onClick={(e) => { e.stopPropagation(); moveFaq(i, -1); }}
                          >
                            <i className="ri-arrow-up-line" />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={i === fq.faqs.length - 1}
                            style={{ padding: '2px 7px' }}
                            title="Move down"
                            onClick={(e) => { e.stopPropagation(); moveFaq(i, 1); }}
                          >
                            <i className="ri-arrow-down-line" />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ padding: '2px 7px' }}
                            onClick={(e) => { e.stopPropagation(); removeFaq(i); }}
                          >
                            <i className="ri-delete-bin-line" />
                          </Button>
                        </div>

                        {/* Q number badge */}
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="badge bg-primary" style={{ fontSize: '11px' }}>Q{i + 1}</span>
                        </div>

                        <Form.Label className="small fw-bold">Question</Form.Label>
                        <Form.Control
                          value={item.q}
                          placeholder="How do I book a flight?"
                          className="mb-2"
                          onChange={e => updateFaq(i, 'q', e.target.value)}
                        />

                        <Form.Label className="small fw-bold">Answer</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={item.a}
                          placeholder="Write a clear and helpful answer..."
                          onChange={e => updateFaq(i, 'a', e.target.value)}
                        />
                      </div>
                    ))}

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-2 fw-bold d-flex align-items-center gap-2"
                      onClick={addFaq}
                    >
                      <i className="ri-add-circle-line" /> Add Question
                    </Button>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 3. BOTTOM CTA ───────────────────────────── */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <i className="ri-customer-service-2-line me-2 text-primary" />
                    <strong>Bottom CTA</strong>
                    <span className="ms-2 text-muted small">— "Still have questions?" block</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="3. CTA Block" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="CTA Title">
                          <Form.Control
                            value={fq.ctaTitle}
                            placeholder="Still have questions?"
                            onChange={e => set('ctaTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="CTA Description">
                          <Form.Control
                            value={fq.ctaText}
                            placeholder="Our support team is happy to assist you..."
                            onChange={e => set('ctaText', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Button Text">
                          <Form.Control
                            value={fq.ctaButton}
                            placeholder="Contact Support"
                            onChange={e => set('ctaButton', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Button Link">
                          <Form.Control
                            value={fq.ctaLink}
                            placeholder="/contact-us"
                            onChange={e => set('ctaLink', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

              </Accordion>
            </Card.Body>

            {/* ── SAVE FOOTER ── */}
            <Card.Footer className="bg-white border-top py-3">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="ri-information-line me-1 text-primary" />
                  {fq.faqs.length} question{fq.faqs.length !== 1 ? 's' : ''} ready to publish.
                </small>
                <Button
                  variant="primary"
                  className="px-5 fw-bold shadow"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'SAVE FAQ ✦'}
                </Button>
              </div>
            </Card.Footer>

          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FaqEditor;